import React, { useState, useEffect } from 'react';
import { listarComprobantes, actualizarComprobante } from '../services/api';
import { toast } from 'react-toastify';
import { DateInput, formatDateToDDMMYYYY } from '../utils/dateUtils';
import { useAuthValidation } from '../hooks/useAuthValidation';

// A comprobante may have no IVA-affected report row needed beyond these fields.
const REPORT_LIMIT = 5000;

const computeRow = (comp) => {
  const totalComprobante = comp.monto_total || 0;
  const iva5 = comp.monto_gravado_5 || 0;
  const iva10 = comp.monto_gravado_10 || 0;
  const iva5pct = Math.round(iva5 * 0.05);
  const iva10pct = Math.round(iva10 * 0.10);
  const irp = totalComprobante - iva5pct - iva10pct;

  return { totalComprobante, iva5, iva10, iva5pct, iva10pct, irp };
};

const ReporteIVA = () => {
  useAuthValidation();
  const [comprobantes, setComprobantes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [filters, setFilters] = useState({
    fecha_inicio: '',
    fecha_fin: '',
    ruc: '',
    nro_timbrado: '',
    nombre_razon_social: '',
  });

  const loadComprobantes = async (activeFilters) => {
    setLoading(true);
    try {
      const data = await listarComprobantes({ ...activeFilters, limit: REPORT_LIMIT });
      setComprobantes(data.comprobantes || []);
    } catch (error) {
      console.error('Error cargando reporte:', error);
      toast.error('Error al cargar el reporte');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadComprobantes(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleFilter = () => {
    loadComprobantes(filters);
  };

  const handleClearFilters = () => {
    const cleared = {
      fecha_inicio: '',
      fecha_fin: '',
      ruc: '',
      nro_timbrado: '',
      nombre_razon_social: '',
    };
    setFilters(cleared);
    loadComprobantes(cleared);
  };

  const formatDate = (value) => formatDateToDDMMYYYY(value);
  const formatMoney = (value) => (value || 0).toLocaleString();

  const handleToggleIncluirIva = async (comp) => {
    const nuevoValor = comp.imputa_iva === 'S' ? 'N' : 'S';
    setUpdatingId(comp.id);
    try {
      await actualizarComprobante(comp.id, {
        nombre_razon_social: comp.nombre_razon_social,
        tipo_comprobante: comp.tipo_comprobante,
        numero_comprobante: comp.numero_comprobante,
        monto_gravado_10: comp.monto_gravado_10,
        monto_gravado_5: comp.monto_gravado_5,
        monto_exento: comp.monto_exento,
        monto_total: comp.monto_total,
        imputa_iva: nuevoValor,
        imputa_ire: comp.imputa_ire,
        imputa_irp_rsp: comp.imputa_irp_rsp,
      });
      setComprobantes((prev) =>
        prev.map((c) => (c.id === comp.id ? { ...c, imputa_iva: nuevoValor } : c))
      );
    } catch (error) {
      console.error('Error actualizando comprobante:', error);
      const message = error.response?.data?.error || 'Error al actualizar el comprobante';
      toast.error(message);
    } finally {
      setUpdatingId(null);
    }
  };

  const rows = comprobantes.map((comp) => ({ comp, ...computeRow(comp) }));

  const totals = rows
    .filter(({ comp }) => comp.imputa_iva === 'S')
    .reduce(
      (acc, row) => ({
        totalComprobante: acc.totalComprobante + row.totalComprobante,
        iva5: acc.iva5 + row.iva5,
        iva10: acc.iva10 + row.iva10,
        iva5pct: acc.iva5pct + row.iva5pct,
        iva10pct: acc.iva10pct + row.iva10pct,
        irp: acc.irp + row.irp,
      }),
      { totalComprobante: 0, iva5: 0, iva10: 0, iva5pct: 0, iva10pct: 0, irp: 0 }
    );

  return (
    <div className="reporte-iva">
      <h3>Reporte IVA</h3>

      {/* Filters */}
      <div className="card mb-4 shadow-sm">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Fecha Inicio</label>
              <DateInput
                name="fecha_inicio"
                value={filters.fecha_inicio}
                onChange={handleFilterChange}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Fecha Fin</label>
              <DateInput
                name="fecha_fin"
                value={filters.fecha_fin}
                onChange={handleFilterChange}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">RUC</label>
              <input
                type="text"
                className="form-control"
                name="ruc"
                value={filters.ruc}
                onChange={handleFilterChange}
                placeholder="Ej: 80012345"
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Timbrado</label>
              <input
                type="text"
                className="form-control"
                name="nro_timbrado"
                value={filters.nro_timbrado}
                onChange={handleFilterChange}
                placeholder="Ej: 18643011"
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Razón Social</label>
              <input
                type="text"
                className="form-control"
                name="nombre_razon_social"
                value={filters.nombre_razon_social}
                onChange={handleFilterChange}
                placeholder="Ej: EMPRESA S.A."
              />
            </div>
            <div className="col-md-8 align-self-end">
              <button className="btn btn-primary me-2" onClick={handleFilter}>
                <i className="bi bi-funnel"></i> Filtrar
              </button>
              <button className="btn btn-secondary" onClick={handleClearFilters}>
                <i className="bi bi-x-circle"></i> Limpiar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Totals */}
      {rows.length > 0 && (
        <div className="card mb-4 shadow-sm">
          <div className="card-body">
            <div className="row g-3 text-center">
              <div className="col">
                <div className="text-muted small">Total Comprobante</div>
                <div className="fw-bold fs-5">{formatMoney(totals.totalComprobante)}</div>
              </div>
              <div className="col">
                <div className="text-muted small">IVA 5</div>
                <div className="fw-bold fs-5">{formatMoney(totals.iva5)}</div>
              </div>
              <div className="col">
                <div className="text-muted small">IVA 10</div>
                <div className="fw-bold fs-5">{formatMoney(totals.iva10)}</div>
              </div>
              <div className="col">
                <div className="text-muted small">IVA 5%</div>
                <div className="fw-bold fs-5">{formatMoney(totals.iva5pct)}</div>
              </div>
              <div className="col">
                <div className="text-muted small">IVA 10%</div>
                <div className="fw-bold fs-5">{formatMoney(totals.iva10pct)}</div>
              </div>
              <div className="col">
                <div className="text-muted small">IRP</div>
                <div className="fw-bold fs-5">{formatMoney(totals.irp)}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="card shadow-sm">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-striped table-hover">
              <thead className="table-dark">
                <tr>
                  <th className="text-center">Incluir</th>
                  <th>RUC</th>
                  <th>Razón Social</th>
                  <th>Fecha</th>
                  <th>Nro. Comprobante</th>
                  <th>Total Comprobante</th>
                  <th>IVA 5</th>
                  <th>IVA 10</th>
                  <th>IVA 5%</th>
                  <th>IVA 10%</th>
                  <th>IRP</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="11" className="text-center">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Cargando...</span>
                      </div>
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="text-center text-muted">
                      <i className="bi bi-inbox"></i>
                      <br />
                      No hay comprobantes para los filtros seleccionados.
                    </td>
                  </tr>
                ) : (
                  rows.map(({ comp, totalComprobante, iva5, iva10, iva5pct, iva10pct, irp }) => (
                    <tr key={comp.id} className={comp.imputa_iva !== 'S' ? 'text-muted' : ''}>
                      <td className="text-center">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={comp.imputa_iva === 'S'}
                          disabled={comp.exportado || updatingId === comp.id}
                          title={comp.exportado ? 'No se puede modificar un comprobante exportado' : ''}
                          onChange={() => handleToggleIncluirIva(comp)}
                        />
                      </td>
                      <td>{comp.identificador}</td>
                      <td>{comp.nombre_razon_social}</td>
                      <td>{formatDate(comp.fecha_comprobante)}</td>
                      <td>{comp.numero_comprobante}</td>
                      <td>{formatMoney(totalComprobante)}</td>
                      <td>{formatMoney(iva5)}</td>
                      <td>{formatMoney(iva10)}</td>
                      <td>{formatMoney(iva5pct)}</td>
                      <td>{formatMoney(iva10pct)}</td>
                      <td>{formatMoney(irp)}</td>
                    </tr>
                  ))
                )}
              </tbody>
              {rows.length > 0 && (
                <tfoot>
                  <tr className="table-secondary fw-bold">
                    <td colSpan="5">Total</td>
                    <td>{formatMoney(totals.totalComprobante)}</td>
                    <td>{formatMoney(totals.iva5)}</td>
                    <td>{formatMoney(totals.iva10)}</td>
                    <td>{formatMoney(totals.iva5pct)}</td>
                    <td>{formatMoney(totals.iva10pct)}</td>
                    <td>{formatMoney(totals.irp)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReporteIVA;
